import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import DescriptionField from './DescriptionField';
import { get12HourFormat } from 'utilities/dateHelper';
import './index.css';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const getTimeMomentObject = (time?: string | null): Moment | null => {
  if (!time) return null;
  return moment(time, 'HH:mm');
};

type TimePickerValue = Moment | null;

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgb(102,102,102)',
          },
          '& .MuiInputBase-input': {
            padding: '0.75rem 0 0.75rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            backgroundColor: '#FFF',
          },
          '&.Mui-error': {
            backgroundColor: '#FFF4C6',
          },
          '&..MuiInputAdornment-root': {
            backgroundColor: '#FFF',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
  },
});

interface ITimePickerProps {
  label?: string | JSX.Element;
  handleTimeChange: (value: string) => void;
  maxTime?: string;
  minTime?: string;
  hasError?: boolean;
  defaultValue?: string | null;
  testID?: string;
  disabled?: boolean;
}

const getTimeMoment = (time?: string | null) => {
  if (!time) {
    return undefined;
  }
  return moment(time, ['HH:mm', 'H:mm'], true);
};

const CustomTimePicker: React.FC<ITimePickerProps> = ({
  defaultValue,
  label,
  minTime,
  maxTime,
  handleTimeChange,
  hasError = false,
  testID,
  disabled = false,
}) => {
  const [time, setTime] = useState<TimePickerValue>(
    defaultValue ? getTimeMomentObject(defaultValue) : null,
  );
  const [open, setOpen] = useState(false);

  const timePickerRef = useRef<HTMLDivElement | null>(null);

  const onTimeChange = useCallback(
    (newTime: Moment | null) => {
      if (newTime?.isValid()) {
        handleTimeChange(newTime.format('HH:mm'));
        setTime(newTime);
        return;
      }

      handleTimeChange('');
      setTime(newTime);
    },
    [handleTimeChange],
  );

  const handleBlur = useCallback(() => {
    if (!time) return;

    const minMomentTime = minTime ? getTimeMomentObject(minTime) : null;
    const maxMomentTime = maxTime ? getTimeMomentObject(maxTime) : null;

    if (minMomentTime && time.isBefore(minMomentTime)) {
      onTimeChange(minMomentTime);
    } else if (maxMomentTime && time.isAfter(maxMomentTime)) {
      onTimeChange(maxMomentTime);
    }
  }, [maxTime, minTime, onTimeChange, time]);

  useEffect(() => {
    setTime(defaultValue ? getTimeMomentObject(defaultValue) : null);
  }, [defaultValue]);

  return (
    <div className="flex flex-col gap-4 w-full" data-testid={testID}>
      <div className="flex w-full flex-col">
        {label && (
          <label className="text-neutral-800 min-h-6 text-base font-semibold">
            {label}
          </label>
        )}
        <div className="relative flex w-full items-center" ref={timePickerRef}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <ThemeProvider theme={theme}>
              <TimePicker
                ampm
                ampmInClock
                disabled={disabled}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => {
                  handleBlur();
                  setOpen(false);
                }}
                closeOnSelect={false}
                value={time}
                onChange={onTimeChange}
                onAccept={() => {
                  handleBlur();
                  setOpen(false);
                }}
                timeSteps={{ minutes: 15 }}
                disableIgnoringDatePartForTimeValidation
                minTime={getTimeMoment(minTime)}
                maxTime={getTimeMoment(maxTime)}
                slotProps={{
                  textField: { error: hasError },
                  openPickerButton: { name: 'open-picker-button' },
                }}
                name="time-input"
              />
            </ThemeProvider>
          </LocalizationProvider>
        </div>
        {minTime && maxTime && (
          <DescriptionField>
            {get12HourFormat(minTime)} - {get12HourFormat(maxTime)}
          </DescriptionField>
        )}
      </div>
    </div>
  );
};

export default CustomTimePicker;
